import { eventApiClient as apiClient, authApiClient } from '@/utils/apiClient';

export class SuperAdminService {
  // 1. Clients & Invitations
  static async getClients() {
    const res = await apiClient.get('/superadmin/clients');
    return res.data.data;
  }

  static async getClientById(id: string) {
    const res = await apiClient.get(`/superadmin/clients/${id}`);
    return res.data.data;
  }

  static async updateClient(id: string, data: any) {
    const res = await apiClient.patch(`/superadmin/clients/${id}`, data);
    return res.data.data;
  }

  static async deleteClient(id: string) {
    const res = await apiClient.delete(`/superadmin/clients/${id}`);
    return res.data;
  }

  static async inviteClient(name: string, email: string, message: string | null) {
    const res = await apiClient.post('/auth/invite-client', { name, email, message });
    return res.data;
  }

  static async deleteInvitation(id: string) {
    const res = await apiClient.delete(`/superadmin/invitations/${id}`);
    return res.data;
  }

  static async revokeInvitation(id: string) {
    const res = await apiClient.patch(`/superadmin/invitations/${id}/revoke`);
    return res.data;
  }

  // 2. Subscriptions
  static async updateSubscription(clientId: string, data: { plan?: 'basic' | 'professional' | 'enterprise'; trial_ends_at?: string | null; discount_applied?: number; status?: 'active' | 'trial' | 'suspended' | 'expired' }) {
    const res = await apiClient.patch(`/superadmin/clients/${clientId}/subscription`, data);
    return res.data.data;
  }

  // 3. Billing
  static async getInvoices() {
    const res = await apiClient.get('/superadmin/billing/invoices');
    return res.data.data;
  }

  static async createManualInvoice(data: { client_id: string; amount: number; tax: number; due_date: string }) {
    const res = await apiClient.post('/superadmin/billing/invoices', data);
    return res.data.data;
  }

  static async payInvoice(id: string, paymentMethod: string) {
    const res = await apiClient.post(`/superadmin/billing/invoices/${id}/pay`, { paymentMethod });
    return res.data.data;
  }

  static async refundInvoice(id: string) {
    const res = await apiClient.post(`/superadmin/billing/invoices/${id}/refund`);
    return res.data.data;
  }

  static async pushInvoice(id: string) {
    const res = await apiClient.post(`/superadmin/billing/invoices/${id}/push`);
    return res.data.data;
  }

  // 4. Global Problem Statement Repositories
  static async getGlobalPsFolders() {
    const res = await apiClient.get('/superadmin/problem-statement-folders');
    return res.data.data;
  }

  static async getGlobalProblemStatements(folderId?: string) {
    const url = folderId && folderId !== 'all'
      ? `/superadmin/global-problem-statements?folder_id=${folderId}`
      : '/superadmin/global-problem-statements';
    const res = await apiClient.get(url);
    return res.data.data;
  }

  static async createGlobalPsFolder(data: { name: string; description?: string; status?: string; is_global?: boolean; assigned_clients?: string[] }) {
    const res = await apiClient.post('/superadmin/problem-statement-folders', { ...data, is_global: data.is_global !== undefined ? data.is_global : true });
    return res.data.data;
  }

  static async deleteGlobalPsFolder(folderId: string) {
    const res = await apiClient.delete(`/superadmin/problem-statement-folders/${folderId}`);
    return res.data;
  }

  static async publishFolder(folderId: string, assignedClients: string[], status: 'DRAFT' | 'SAVED' | 'PUBLISHED' = 'PUBLISHED') {
    const res = await apiClient.put(`/superadmin/problem-statement-folders/${folderId}/publish`, {
      assigned_clients: assignedClients,
      status
    });
    return res.data;
  }

  static async bulkUploadExcel(filename: string, items: any[]) {
    const res = await apiClient.post('/superadmin/global-problem-statements/bulk-upload', {
      filename,
      items
    });
    return res.data;
  }

  // 5. Support Tickets
  static async getTickets() {
    const res = await apiClient.get('/superadmin/support/tickets');
    return res.data.data;
  }

  static async updateTicket(id: string, data: { status?: 'open' | 'in_progress' | 'resolved' | 'closed'; assigned_agent?: string | null }) {
    const res = await apiClient.patch(`/superadmin/support/tickets/${id}`, data);
    return res.data.data;
  }

  // 5. Announcements
  static async getAnnouncements() {
    const res = await apiClient.get('/superadmin/announcements');
    return res.data.data;
  }

  static async createAnnouncement(data: { title: string; content: string; type: 'maintenance' | 'release' | 'emergency' | 'general'; target_clients: string }) {
    const res = await apiClient.post('/superadmin/announcements', data);
    return res.data.data;
  }

  // 6. Reports
  static async getReports() {
    const res = await apiClient.get('/superadmin/reports');
    return res.data.data;
  }

  // 7. Platform Settings
  static async getSettings() {
    const res = await apiClient.get('/superadmin/settings');
    return res.data.data;
  }

  static async updateSettings(data: any) {
    const res = await apiClient.patch('/superadmin/settings', data);
    return res.data.data;
  }

  // 8. Audit Logs
  static async getAuditLogs() {
    const res = await apiClient.get('/superadmin/audit-logs');
    return res.data.data;
  }

  // 9. Subscription Plans
  static async getPlans() {
    const res = await apiClient.get('/superadmin/plans');
    return res.data.data;
  }

  static async createPlan(data: any) {
    const res = await apiClient.post('/superadmin/plans', data);
    return res.data.data;
  }

  static async updatePlan(id: string, data: any) {
    const res = await apiClient.patch(`/superadmin/plans/${id}`, data);
    return res.data.data;
  }

  static async deletePlan(id: string) {
    const res = await apiClient.delete(`/superadmin/plans/${id}`);
    return res.data;
  }

  // 10. Active Sessions & Device Security Management
  static async getActiveSessions() {
    try {
      const res = await apiClient.get('/superadmin/sessions');
      return res.data?.sessions || res.data?.data || [];
    } catch {
      try {
        const res2 = await authApiClient.get('/api/auth/sessions');
        return res2.data?.sessions || res2.data?.data || [];
      } catch {
        return [];
      }
    }
  }

  static async revokeSession(sessionId: string) {
    try {
      const res = await apiClient.delete(`/superadmin/sessions/${sessionId}`);
      return res.data;
    } catch {
      const res2 = await authApiClient.delete(`/api/auth/sessions/${sessionId}`);
      return res2.data;
    }
  }

  static async revokeOtherSessions() {
    try {
      const res = await apiClient.delete('/superadmin/sessions-revoke-other');
      return res.data;
    } catch {
      const res2 = await authApiClient.delete('/api/auth/sessions-revoke-other');
      return res2.data;
    }
  }

  // 11. Sthitih Mentors (Global Industry Mentors)
  static async getSthitihMentors() {
    const res = await apiClient.get('/superadmin/sthitih-mentors');
    return res.data.data;
  }

  static async inviteSthitihMentor(data: { name: string; email: string; client_ids?: string[]; message?: string; mentor_type?: string }) {
    const res = await apiClient.post('/superadmin/sthitih-mentors/invite', data);
    return res.data;
  }

  static async grantMentorInstitutes(mentorId: string, clientIds: string[]) {
    const res = await apiClient.post(`/superadmin/sthitih-mentors/${mentorId}/grant-institutes`, { client_ids: clientIds });
    return res.data;
  }

  static async getClientSthitihMentors(clientId: string) {
    const res = await apiClient.get(`/superadmin/clients/${clientId}/sthitih-mentors`);
    return res.data.data;
  }

  static async assignMentorsToClient(clientId: string, mentorIds: string[]) {
    const res = await apiClient.post(`/superadmin/clients/${clientId}/sthitih-mentors`, { mentor_ids: mentorIds });
    return res.data;
  }

  static async removeMentorFromClient(clientId: string, mentorId: string) {
    const res = await apiClient.delete(`/superadmin/clients/${clientId}/sthitih-mentors/${mentorId}`);
    return res.data;
  }

  static async revokeSthitihMentorInvitation(invitationId: string) {
    const res = await apiClient.delete(`/superadmin/sthitih-mentors/invitations/${invitationId}`);
    return res.data;
  }

  static async removeSthitihMentor(mentorId: string) {
    const res = await apiClient.delete(`/superadmin/sthitih-mentors/${mentorId}`);
    return res.data;
  }

  static async blacklistEmail(data: { email: string; reason?: string }) {
    const res = await apiClient.post('/superadmin/sthitih-mentors/blacklist', data);
    return res.data;
  }

  static async getBlacklistedEmails() {
    const res = await apiClient.get('/superadmin/sthitih-mentors/blacklist');
    return res.data.data;
  }

  static async removeBlacklist(id: string) {
    const res = await apiClient.delete(`/superadmin/sthitih-mentors/blacklist/${id}`);
    return res.data;
  }
}
